/**
 * OWA ArtFinder — Custom element for AI-powered art search overlay
 */
if (!customElements.get('owa-artfinder')) {
  class OwaArtfinder extends HTMLElement {
    constructor() {
      super();
      this.backdrop = this.querySelector('.artfinder__backdrop');
      this.panel = this.querySelector('.artfinder__panel');
      this.closeBtn = this.querySelector('.artfinder__close');
      this.form = this.querySelector('.artfinder__form');
      this.input = this.querySelector('[data-artfinder-input]');
      this.submitBtn = this.querySelector('[data-artfinder-submit]');
      this.loading = this.querySelector('[data-artfinder-loading]');
      this.examples = this.querySelectorAll('[data-example]');
      this.workerUrl = this.dataset.workerUrl || '';
      const routesRoot =
        window.Shopify &&
        window.Shopify.routes &&
        typeof window.Shopify.routes.root === 'string'
          ? window.Shopify.routes.root
          : '/';
      const normalizedRoot = routesRoot.endsWith('/') ? routesRoot : `${routesRoot}/`;
      this.searchUrl = this.dataset.searchUrl || `${normalizedRoot}search`;
    }

    connectedCallback() {
      if (!this.form || !this.input) return;

      /* Close on click outside panel (backdrop or container itself) */
      this.addEventListener('click', (e) => {
        if (e.target === this || e.target === this.backdrop) this.close();
      });
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleSubmit();
      });

      this.examples.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.input.value = btn.dataset.example;
          this.input.focus();
        });
      });
    }

    isOpen() {
      return this.getAttribute('aria-hidden') === 'false';
    }

    open() {
      this.style.display = 'flex';
      /* Force reflow so transition triggers */
      this.offsetHeight;
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(() => this.input.focus(), 300);
    }

    close() {
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      /* Hide after fade-out transition (300ms) */
      setTimeout(() => {
        if (this.getAttribute('aria-hidden') === 'true') {
          this.style.display = 'none';
        }
      }, 350);
    }

    async handleSubmit() {
      const description = this.input.value.trim();
      if (!description) {
        this.input.focus();
        return;
      }

      const orientationEl = this.querySelector('input[name="orientation"]:checked');
      const orientation = orientationEl ? orientationEl.value : '';

      /* If no Worker URL configured, fall back to direct Shopify search */
      if (!this.workerUrl) {
        this.redirectToSearch(description, orientation);
        return;
      }

      /* Show loading state */
      this.form.style.display = 'none';
      this.loading.style.display = '';
      this.submitBtn.disabled = true;

      try {
        const res = await fetch(this.workerUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ description, orientation }),
        });

        if (!res.ok) throw new Error('Worker error');

        const data = await res.json();
        const keywords = data.keywords || description;
        this.redirectToSearch(keywords, orientation);
      } catch (err) {
        void err;
        /* Fallback: use original description as search query */
        this.redirectToSearch(description, orientation);
      }
    }

    redirectToSearch(query, orientation) {
      const params = new URLSearchParams();
      params.set('q', query);
      params.set('type', 'product');
      if (orientation) {
        params.set('tag', orientation);
      }
      window.location.href = `${this.searchUrl}?${params.toString()}`;
    }
  }

  customElements.define('owa-artfinder', OwaArtfinder);
}

if (!window.__owaArtfinderTriggersBound) {
  window.__owaArtfinderTriggersBound = true;

  const syncArtfinderState = () => {
    const hasOverlay = Boolean(document.querySelector('owa-artfinder'));
    document.documentElement.classList.toggle('owa-artfinder-enabled', hasOverlay);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncArtfinderState, { once: true });
  } else {
    syncArtfinderState();
  }

  document.addEventListener('shopify:section:load', syncArtfinderState);
  document.addEventListener('shopify:section:unload', syncArtfinderState);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-artfinder-trigger]');
    if (!trigger) return;

    event.preventDefault();

    const artfinder = document.querySelector('owa-artfinder');
    if (!artfinder || typeof artfinder.open !== 'function') return;

    artfinder.open();
  });
}
